/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useRef} from 'react';
import APM from '../APM';
import getMonitor from '../monitors/PagePerformanceMonitor';

// provides a reference to the number of rendered `PageMonitorComplete` instances
let instances = 0;

/**
 * Provides a utility for determining the number of `<PageMonitorComplete />`
 * instances that are pending to be mounted.
 *
 * @returns the number of `<PageMonitorComplete/>` instances that are pending
 *          to be mounted
 */
export const getInstances = (): number => instances;

/**
 * Provides a convenience function for determining if the APM Framework is
 * enabled. This function is intended for allowing components to omit rendering
 * the `PageMonitorComplete` component if the APM Framework is not enabled.
 *
 * ```typescript
 *   import PageMonitorComplete, {
 *     isAPMEnabled
 *   } from '@ion/telemetry/src/apm/automation/PageMonitorComplete';
 *
 *   // only render <PageMonitorComplete /> when APM framework is enabled ...
 *   const SomeComponent = ({...}) => {
 *     ...
 *     return (
 *       <div>
 *          ...
 *         <ConditionalRender condition={isAPMEnabled}>
 *           <PageMonitorComplete autoComplete />
 *         </ConditionalRender>
 *       </div
 *     );
 *   };
 * ```
 *
 * @returns `true` if the APM framework is currently enabled, otherwise,
 *          `false`.
 */
export const isAPMEnabled = (): boolean => (
  APM.isEnabled
);

/**
 * The `PageMonitorComplete` component allows for automatically completing page
 * monitoring based on the number of ``PageMonitorComplete` components rendered
 * within the page hierarchy. Once all instances have been rendered, the stop
 * method will automatically be invoked on the `PagePerformanceMonitor`
 * API.
 *
 * In the following examples, 3 `PageMonitorComplete` components have been
 * rendered, thus, upon mounting of the last instance, the `stop` method of
 * the `PagePerformanceMonitor` API will automatically be invoked, and the
 * instances count will be reset to zero (in preparation for the next page).
 *
 * ```typescript
 *   import PageMonitorComplete from '@ion/telemetry/src/apm/automation/PageMonitorComplete';
 *   ...
 *
 *   return ()
 *     <div className={classes.container}>
 *       <ul>
 *         <li>
 *          <PageMonitorComplete />
 *         </li>
 *         <li>
 *          <PageMonitorComplete />
 *         </li>
 *         <li>
 *          <PageMonitorComplete />
 *         </li>
 *       </ul>
 *     </div>
 *   );
 * ```
 *
 * The `autoComplete` prop can be set to true in order to automatically complete
 * page monitoring. This prop should only be used to explicitly complete page
 * monitoring when the component renders, and will override any pending instances.
 *
 * ```typescript
 *   import PageMonitorComplete from '@ion/telemetry/src/apm/automation/PageMonitorComplete';
 *   ...
 *
 *   return ()
 *     <div className={classes.container}>
 *       <PageMonitorComplete autoComplete />
 *       ...
 *     </div>
 *   );
 * ```
 *
 */
const PageMonitorComplete = ({autoComplete = false}) => {
  const instance = useRef(APM.isEnabled ? ++instances : 0);

  const onComplete = useCallback(() => {
    if (APM.isEnabled) {
      const monitor = getMonitor();
      monitor?.isRunning() && monitor?.stop();
      instances = 0;
    }
  }, []);

  useEffect(() => {
    if (autoComplete) {
      onComplete();
    }
  }, [autoComplete]);

  useEffect(() => {
    if (instance.current === instances) {
      onComplete();
    }
  }, []);
  return undefined;
};
export default React.memo(PageMonitorComplete);
